import clsx from 'clsx'
import {find} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {useAuth} from '../../../../../auth'
import useCancelToken from '../../../../../../../_gori/hooks/UseCancelToken'
import {useA1BatchesProvider} from '../../../../../../../_gori/providers/A1BatchesProvider'
import A1BatchService from '../../../../core/_requests'
import A1_CONSTANTS from '../../../../core/_const'
import {Button, InputSearch, SelectFormik} from '../../../../../../../_gori/partials/widgets'
import {DEFAULT_PAGE, DEFAULT_PAGE_SIZE, FORMAT_DATE} from '../../../../../../../_gori/constants'
import {DatePicker} from '../../../../../../../_gori/helpers/components/DatePicker'
import {convertUserTimeZone, KTSVG} from '../../../../../../../_gori/helpers'
import {textByCount} from '../../../../../../../_gori/utils'
import {mappingStatusTicket} from '../../../../utils'

type Props = {
  show: boolean
  handleCloseModal: () => void
}

const TicketList: React.FC<Props> = ({show, handleCloseModal}) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const {newCancelToken, isCancel} = useCancelToken()

  const {a1Batch, setTicketForum} = useA1BatchesProvider()
  const [tableData, setTableData] = useState<{
    tickets: any
    pagination: any
    hasLoadMore: boolean
  }>({tickets: [], pagination: {}, hasLoadMore: false})
  const [loading, setLoading] = useState<{first: boolean; getTickets: boolean; refresh: boolean}>({
    first: true,
    getTickets: false,
    refresh: false,
  })
  const pageCurrent = useRef(Number(DEFAULT_PAGE))
  const [totalTabUnread, setTotalTabUnread] = useState({...a1Batch?.ticket_unread_count})

  const tabs = useMemo(() => {
    return [
      {key: 'origin', label: intl.formatMessage({id: 'ORIGIN_PARTNER'})},
      {
        key: 'destination',
        label: intl.formatMessage({id: 'DESTINATION_PARTNER'}),
      },
    ]
  }, [intl])

  const tabName = useMemo(() => {
    return (searchParams.get('sender') || 'origin') as 'origin' | 'destination'
  }, [searchParams])

  const configParams = useMemo(
    () => ({
      page: DEFAULT_PAGE,
      page_size: DEFAULT_PAGE_SIZE,
      search_text: searchParams.get('search_text') || null,
      status: searchParams.get('status') || null,
      sender: tabName,
      date_from: searchParams.get('date_from') || null,
      date_to: searchParams.get('date_to') || null,
    }),
    [searchParams, tabName]
  )

  const setTable = useCallback(
    async (refresh = false) => {
      pageCurrent.current = Number(DEFAULT_PAGE)

      try {
        setLoading((prev) => ({...prev, [refresh ? 'refresh' : 'first']: true}))

        const config = {
          params: configParams,
          cancelToken: newCancelToken(),
        }
        const response: any = await A1BatchService.getTickets(a1Batch?.id, config)
        if (response) {
          setTableData({...response, hasLoadMore: response.pagination.total_pages > 1})
          setTotalTabUnread((prev) => ({
            ...prev,
            [configParams.sender]: response.total_comment_unread,
          }))
        }
      } catch (error: any) {
        if (isCancel(error)) return
      } finally {
        setLoading((prev) => ({...prev, [refresh ? 'refresh' : 'first']: false}))
      }
    },
    [a1Batch?.id, configParams, isCancel, newCancelToken]
  )

  const fetchMore = async () => {
    if (pageCurrent.current >= tableData?.pagination?.total_pages) {
      setTableData((prev) => ({...prev, hasLoadMore: false}))
      return
    }

    pageCurrent.current++

    try {
      setLoading((prev) => ({...prev, getTickets: true}))
      const config = {
        params: {...configParams, page: pageCurrent.current},
        cancelToken: newCancelToken(),
      }
      const response: any = await A1BatchService.getTickets(a1Batch.id, config)
      if (response) {
        setTableData((prev) => ({...prev, tickets: [...prev.tickets, ...response.tickets]}))
      }
    } catch (error: any) {
      if (isCancel(error)) return
    } finally {
      setLoading((prev) => ({...prev, getTickets: false}))
    }
  }

  useEffect(() => {
    if (show && a1Batch?.id) {
      setTable()
    }
  }, [a1Batch?.id, setTable, show])

  const filterStatus = useMemo(() => {
    const statusValue = searchParams.get('status')
    const status: any =
      find(Object.values(A1_CONSTANTS.TICKET_STATUS), {value: statusValue}) || null

    return {
      label: intl.formatMessage({id: status ? status.label : 'ALL'}),
      value: statusValue || '',
    }
  }, [intl, searchParams])

  const handleFilterStatus = useCallback(
    (e) => {
      if (e.value) {
        searchParams.set('status', e.value)
      } else {
        searchParams.delete('status')
      }
      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams]
  )

  const handleViewDetail = (ticket) => {
    searchParams.set('id', ticket.id.toString())
    setSearchParams(searchParams)
    setTicketForum((prev) => ({...prev, detailId: ticket.id.toString()}))
  }

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center px-5 py-6'>
        <h4 className='fw-bolder fs-1 m-0'> {intl.formatMessage({id: 'TICKET_FORUM'})}</h4>
        <div className='d-flex gap-8 align-items-center'>
          <Button
            className={clsx(
              'd-flex align-items-center justify-content-center btn btn-advanced-tertiary btn-sm p-1 cursor-pointer',
              {
                'text-primary': loading.refresh,
              }
            )}
            event={() => {
              setTable(true)
            }}
            disabled={loading.refresh}
          >
            <KTSVG
              path='/media/gori/a1/sync.svg'
              className={clsx('svg-icon-1 text-hover-primary', {
                'spin_reverse text-primary': loading.refresh,
              })}
            />
            {intl.formatMessage({id: 'REFRESH'})}
          </Button>
          <Button
            className='btn btn-icon border border-1 border-gray-300 btn-active-light-primary'
            event={handleCloseModal}
          >
            <KTSVG path='/media/gori/orders/delete.svg' className='svg-icon-4 text-gray-700' />
          </Button>
        </div>
      </div>
      <div className='ticket-forum-body d-flex flex-column'>
        <div className='d-flex flex-column flex-md-row justify-content-between flex-wrap gap-4 px-5 py-2 ticket-filter border border-gray-300 border-2'>
          <div className='d-flex align-items-center position-relative my-1'>
            <InputSearch
              keySearch='search_text'
              className='min-w-250px bg-white border-gray-300'
              placeholder={`${intl.formatMessage({id: 'SEARCH_TICKET'})}...`}
            />
          </div>
          <div className='d-flex flex-column flex-sm-row align-items-center gap-4'>
            <SelectFormik
              className='w-100 fw-bold fs-5'
              value={filterStatus}
              placeholder={intl.formatMessage({id: 'STATUS'})}
              onChange={handleFilterStatus}
              options={Object.values(A1_CONSTANTS.TICKET_STATUS)}
              hasUseIntl={true}
              emptyDefault={false}
            />
            <DatePicker
              dateFrom='date_from'
              dateTo='date_to'
              name={intl.formatMessage({id: 'DATE'})}
              className='w-100 h-45px border-gray-300'
            />
          </div>
        </div>
        <div
          className='sticky-top mx-0 d-grid'
          style={{gridTemplateColumns: `repeat(${tabs.length}, 1fr)`}}
        >
          {tabs.map((tab) => (
            <div
              className={clsx(
                'd-flex gap-4 justify-content-center align-items-center py-5 border-bottom border-4 cursor-pointer',
                {
                  'bg-light-primary border-primary': tabName === tab?.key,
                  'bg-transparent border-gray-300': tabName !== tab?.key,
                }
              )}
              key={tab?.key}
              onClick={() => {
                searchParams.set('sender', tab?.key)
                setSearchParams(searchParams)
              }}
            >
              <h4
                className={clsx('fw-bolder', {
                  'text-dark': tabName === tab?.key,
                  'text-gray-600': tabName !== tab?.key,
                })}
              >
                {`${tab?.label} `}
              </h4>

              {totalTabUnread[tab?.key] > 0 && (
                <div
                  className={clsx('px-3 rounded-3 fs-7 h-20px d-flex align-items-center', {
                    'text-white bg-danger border border-danger': tabName === tab?.key,
                    'text-danger bg-light-danger border border-danger': tabName !== tab?.key,
                  })}
                  style={{lineHeight: '20px'}}
                >
                  {tabName !== tab?.key
                    ? totalTabUnread?.[tab?.key]
                    : `${totalTabUnread[tab?.key]} ${intl
                        .formatMessage({
                          id: 'NEW',
                        })
                        .toLocaleLowerCase()} ${textByCount(
                        totalTabUnread?.[tab?.key],
                        intl.formatMessage({id: 'MESSAGE'}),
                        intl.formatMessage({id: 'MESSAGES'})
                      ).toLocaleLowerCase()}`}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className='flex-fill d-flex flex-column scroll-y scrollbar-width-none'>
          {loading.first || loading.refresh ? (
            <div className='mt-5'>
              {Array.from({length: 3}).map((_, idx) => (
                <div
                  className='d-flex justify-content-between align-items-center p-4 mt-4 border border-gray-300 rounded-4 mx-9'
                  key={idx}
                >
                  <div className='col-10 d-flex flex-column'>
                    <div className='fs-1 col-6 placeholder rounded-2 bg-secondary' />
                    <span className='fs-1 col-4 placeholder placeholder-sm rounded-2 bg-secondary mt-2' />
                  </div>
                  <span className='fs-1 col-2 placeholder placeholder-sm rounded-2 bg-secondary mt-2' />
                </div>
              ))}
            </div>
          ) : tableData?.tickets.length > 0 ? (
            <InfiniteScroll
              dataLength={tableData?.tickets}
              next={fetchMore}
              hasMore={tableData?.hasLoadMore}
              height={'75vh'}
              loader={
                loading.getTickets && (
                  <div className='d-flex justify-content-center'>
                    <span className='spinner-border' />
                  </div>
                )
              }
              className='py-9'
            >
              {tableData?.tickets.map((ticket: any, idx) => {
                const status = mappingStatusTicket(ticket?.status)

                const ticketDate = convertUserTimeZone(
                  ticket.created_at,
                  currentUser,
                  FORMAT_DATE.DATE_TIME
                )
                return (
                  <div
                    className={clsx(
                      'position-relative overflow-hidden d-flex justify-content-between align-items-center p-4 mb-5 border border-gray-300 rounded-4 cursor-pointer border-hover-primary bg-hover-light-primary mx-9',
                      {
                        'opacity-75': status === 'closed',
                      }
                    )}
                    key={idx}
                    onClick={() => handleViewDetail(ticket)}
                  >
                    {ticket?.total_comment_unread > 0 && (
                      <div className='w-14px h-100 position-absolute start-0 rounded-start bg-primary' />
                    )}
                    <div
                      className={clsx('me-10 text-truncate align-items-center', {
                        'ms-4': !!ticket?.total_comment_unread,
                        'ms-2': !ticket?.total_comment_unread,
                      })}
                    >
                      <div className='d-flex align-items-center mb-1'>
                        <h4 className='text-truncate mh-0'>
                          {intl.formatMessage({id: 'TICKET'})} #{ticket?.id} - {ticket?.name}
                        </h4>
                        {!!ticket?.total_comment_unread && (
                          <span
                            className={
                              'px-3 rounded-3 fs-7 text-white bg-danger ms-2 d-flex justify-content-center align-items-center h-20px'
                            }
                          >
                            {ticket.total_comment_unread}
                          </span>
                        )}
                      </div>
                      <span className='text-muted'>
                        {`${intl.formatMessage({id: 'CREATED_ON'})} ${ticketDate}`}
                      </span>
                    </div>
                    <div>
                      <span className={`badge-${status}-ticket rounded-pill px-2`}>
                        {intl.formatMessage({id: status.toUpperCase()})}
                      </span>
                    </div>
                  </div>
                )
              })}
            </InfiniteScroll>
          ) : (
            <div className='flex-fill mt-2 d-flex  flex-column justify-content-center align-items-center gap-5'>
              <div className='d-flex justify-content-between align-items-center p-4 mt-4 border border-gray-300 rounded-2 w-50'>
                <div className='col-10 d-flex flex-column'>
                  <div className='fs-1 col-6 placeholder rounded-2 bg-secondary' />
                  <span className='fs-1 col-4 placeholder placeholder-sm rounded-2 bg-secondary mt-2' />
                </div>
                <span className='fs-1 col-2 placeholder placeholder-sm rounded-2 bg-secondary mt-2' />
              </div>
              <h1>{intl.formatMessage({id: 'NO_TICKET_FOUND'})}</h1>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export {TicketList}
